import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { equipmentApi, repairRequestsApi } from '@/api';
import CompanySelect from '@/components/shared/CompanySelect';
import { toast } from '@/hooks/use-toast';


const requestSchema = z.object({
  company: z.string().min(1, 'Please select a company'),
  equipmentId: z.string().min(1, 'Please select equipment'),
  driverName: z.string().max(256).optional(),
  requestDate: z.string().min(1, 'Date is required'),
  problem: z.string().min(10, 'Please describe the problem (min 10 characters)'),
});

type RequestFormData = z.infer<typeof requestSchema>;

interface RequestFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function RequestForm({ onSuccess, onCancel }: RequestFormProps) {
  const [photos, setPhotos] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser, hasRole } = useAuth();
  const isSubmitter = hasRole(['submitter']);
  const queryClient = useQueryClient();

  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time

  const form = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      company: '',
      equipmentId: '',
      driverName: currentUser?.name ?? '',
      requestDate: today,
      problem: '',
    },
  });

  const selectedCompany = form.watch('company');
  const selectedEquipmentId = form.watch('equipmentId');

  // When company changes, reset equipment selection
  useEffect(() => {
    form.setValue('equipmentId', '');
  }, [selectedCompany, form]);

  // Fetch equipment filtered by company + active status
  const { data: equipment, isLoading: loadingEquipment } = useQuery({
    queryKey: ['equipment', 'active', selectedCompany],
    queryFn: () =>
      equipmentApi.list({ status: 'Active', company: selectedCompany, pageSize: 200 }),
    enabled: !!selectedCompany,
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => repairRequestsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repairRequests'] });
      toast({ title: 'Repair request submitted!' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Failed to submit request', variant: 'destructive' });
    },
  });

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setPhotos((prev) => [...prev, ...files].slice(0, 5));
  }

  function removePhoto(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  }

  const selectedEquipment = equipment?.items.find((eq) => String(eq.id) === selectedEquipmentId);

  async function onSubmit(values: RequestFormData) {
    await createMutation.mutateAsync({
      equipmentId: Number(values.equipmentId),
      driverName: values.driverName || undefined,
      truckOrTrailer: selectedEquipment?.equipmentType ?? undefined,
      problem: values.problem,
      requestDate: values.requestDate,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {/* Row 1: Who's This For + Date (read-only) */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="driverName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Who's this for?</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Full name"
                    {...field}
                    disabled={isSubmitter}
                    className={isSubmitter ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <p className="text-sm font-medium leading-none">Repair Order Date</p>
            <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
              {today}
            </div>
          </div>
        </div>

        {/* Row 2: Company + Equipment */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <CompanySelect
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="equipmentId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Equipment <span className="text-destructive">*</span></FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={loadingEquipment || !selectedCompany}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={loadingEquipment ? 'Loading…' : 'Select unit…'}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {equipment?.items.map((eq) => (
                      <SelectItem key={eq.id} value={String(eq.id)}>
                        {eq.unitNumber} — {eq.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Only active {selectedCompany} equipment is shown.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Selected equipment details */}
        {selectedEquipment && (
          <div className="flex gap-3 rounded-md border border-border bg-muted/40 p-3">
            {selectedEquipment.photoUrl ? (
              <img
                src={selectedEquipment.photoUrl}
                alt={selectedEquipment.unitNumber}
                className="h-16 w-16 flex-shrink-0 rounded-md object-cover border border-border"
              />
            ) : (
              <div className="h-16 w-16 flex-shrink-0 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground text-xs">
                No photo
              </div>
            )}
            <div className="space-y-0.5 text-sm">
              <p className="font-semibold">{selectedEquipment.unitNumber}</p>
              <p className="text-muted-foreground">{selectedEquipment.equipmentType}</p>
              {selectedEquipment.description && (
                <p className="text-muted-foreground">{selectedEquipment.description}</p>
              )}
            </div>
          </div>
        )}

        {/* Problem or Issue */}
        <FormField
          control={form.control}
          name="problem"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Problem or Issue</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the issue in detail — what you noticed, when it started, any sounds or symptoms…"
                  rows={5}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Photo upload */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Photo of Issue <span className="text-muted-foreground font-normal">(optional, max 5)</span>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={handlePhotoChange}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={photos.length >= 5}
          >
            <Camera className="mr-2 h-4 w-4" />
            {photos.length === 0 ? 'Select files…' : `Add More (${photos.length}/5)`}
          </Button>
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {photos.map((photo, i) => (
                <div
                  key={i}
                  className="group relative flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs"
                >
                  <span className="max-w-[120px] truncate">{photo.name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => removePhoto(i)}
                    aria-label={`Remove ${photo.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
